import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Spin, Alert, Empty } from 'antd';
import CategoryTile from '../components/CategoryTile';
import { useOnboardingStore } from '../stores/onboarding-store';
import {useGetCategoriesForUserGroup} from "@/shared/api/client/customer-category-controller";
import {CustomerCategoryDTO} from "@/shared/api/model";

const { Title, Paragraph } = Typography;

const CategorySelectionView: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedCategory } = useOnboardingStore();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null|undefined>(null);

  const {
    data: userCategoryAccessResponse,
    isLoading,
    error,
  } = useGetCategoriesForUserGroup();

  const userCategoryAccess = userCategoryAccessResponse;

  const handleCategorySelect = (category: CustomerCategoryDTO) => {
    setSelectedCategoryId(category.id);
    setSelectedCategory(category);
    
    // Navigate based on customer type
    const customerType = category.customerType?.toLowerCase();
    switch (customerType) {
      case 'individual':
        navigate(`/customers/onboarding/individual/${category.id}`);
        break;
      case 'group':
        navigate(`/customers/onboarding/group/${category.id}`);
        break;
      case 'business':
        navigate(`/customers/onboarding/business/${category.id}`);
        break;
      default:
        console.error('Unknown customer type:', category.customerType);
    }
  };

  if (isLoading) {
    return (
      <div className="category-selection-loading">
        <Spin size="large" />
        <Paragraph>Loading customer categories...</Paragraph>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Categories"
        description="Unable to load customer categories. Please try again later."
        type="error"
        showIcon
      />
    );
  }

  const categories = userCategoryAccess?.availableCategories || [];

  if (categories.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No customer categories available for your user group"
      />
    );
  }

  return (
    <div className="category-selection-view">
      <div className="category-selection-header">
        <Title level={3}>Select Customer Category</Title>
        <Paragraph>
          Choose the type of customer you want to register. Each category has specific 
          requirements and configurations.
        </Paragraph>
      </div>

      <Row gutter={[24, 24]} className="category-grid">
        {categories.map((category) => (
          <Col
            key={category.id}
            xs={24}
            sm={12}
            md={8}
            lg={8}
            xl={6}
            xxl={4}
          >
            <CategoryTile
              category={category}
              onClick={handleCategorySelect}
              isSelected={selectedCategoryId === category.id}
              disabled={category.status!=='ACTIVE'}
            />
          </Col>
        ))}
      </Row>

        {/*@ts-ignore*/}
      {userCategoryAccess?.hasFullAccess && (
        <div className="category-access-info">
          <Paragraph type="secondary">
            You have full access to all customer categories in your user group.
          </Paragraph>
        </div>
      )}
    </div>
  );
};

export default CategorySelectionView;
